import Vue from 'vue';
import axios from '@/plugins/axios';
import storeUtils from '@/stores/utils';
import store from '@/stores/store';
import { replaceOrderVariables } from '@/utils/order-variables';
import {
    TEMPLATE_SOURCE,
    defaultTemplateSource,
    makeVendorForwardOrderSubject,
    makeVendorForwardOrderMessage,
    vendorForwardI18n,
} from '@/utils/invoice-email';

function translateVendor(key, values) {
    const locale = Vue.prototype.$i18n?.locale || 'fr';
    const dict = vendorForwardI18n[locale] || vendorForwardI18n.en;
    let text = dict[key] || vendorForwardI18n.en[key] || key;
    if (values) {
        Object.keys(values).forEach((k) => {
            text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), values[k] ?? '');
        });
    }
    return text;
}

const state = {
    billingaccount: null,
    customer_email_available: false,
    customer: null,
    order: null,
    invoices: [],
    invoices_loading: false,
    message: null,
    receive_copy: false,
    send_copy_to_billing_emails: false,
    recipients: [],
    subject: null,
    sending_invoice_errors: {},
    sending_invoice: false,
    reminder: false,
    template_source: TEMPLATE_SOURCE.BILLING_ACCOUNT,
};

const getters = {
    ready: (state) => {
        return state.message && state.subject && state.recipients?.length;
    },
};

function agentEntityIdFor(order) {
    return order?.agent || store.getters['session/current_entity_id'];
}

function makeBillingAccountSubject(order, billingaccount, reminder = false) {
    const subjectTemplate = reminder
        ? order.invoices_count > 1
            ? billingaccount?.notification_texts?.orderinvoices_reminder_subject
            : billingaccount?.notification_texts?.orderinvoice_reminder_subject
        : order.invoices_count > 1
          ? billingaccount?.notification_texts?.orderinvoices_notification_subject
          : billingaccount?.notification_texts?.orderinvoice_notification_subject;

    return replaceOrderVariables(
        subjectTemplate,
        order,
        billingaccount,
        Vue.prototype.$translate
    );
}

function makeBillingAccountMessage(order, billingaccount, reminder = false) {
    const messageTemplate = reminder
        ? order.invoices_count > 1
            ? billingaccount?.notification_texts?.orderinvoices_reminder_text
            : billingaccount?.notification_texts?.orderinvoice_reminder_text
        : order.invoices_count > 1
          ? billingaccount?.notification_texts?.orderinvoices_notification_text
          : billingaccount?.notification_texts?.orderinvoice_notification_text;
    return replaceOrderVariables(
        messageTemplate,
        order,
        billingaccount,
        Vue.prototype.$translate
    );
}

function invoicesCount(state) {
    return state.invoices?.length || state.order?.invoices_count || 1;
}

function applyTemplates(state) {
    if (!state.order) {
        state.subject = null;
        state.message = null;
        return;
    }
    const count = invoicesCount(state);
    if (state.template_source === TEMPLATE_SOURCE.VENDOR_FORWARD) {
        state.subject = makeVendorForwardOrderSubject(
            state.order,
            count,
            translateVendor
        );
        state.message = makeVendorForwardOrderMessage(
            state.order,
            count,
            translateVendor
        );
        return;
    }
    state.subject = makeBillingAccountSubject(
        state.order,
        state.billingaccount,
        state.reminder
    );
    state.message = makeBillingAccountMessage(
        state.order,
        state.billingaccount,
        state.reminder
    );
}

function refreshTemplateSource(state) {
    state.template_source = defaultTemplateSource(
        state.invoices,
        agentEntityIdFor(state.order)
    );
    applyTemplates(state);
}

const mutations = {
    updateOrder(state, order) {
        state.order = order;
        state.sending_invoice = false;
        state.sending_invoice_errors = {};
        state.customer = order?.buyer_data;
        state.customer_email_available = !!state.customer?.billing_address?.emails;
        state.invoices = [];
        refreshTemplateSource(state);
    },

    updateInvoices(state, invoices) {
        state.invoices = invoices || [];
        refreshTemplateSource(state);
    },

    updateInvoicesLoading(state, loading) {
        state.invoices_loading = loading;
    },

    updateSendingInvoice(state, sending) {
        state.sending_invoice = sending;
        state.sending_invoice_errors = {};
    },
    updateSendingInvoiceErrors(state, sending_invoice_errors) {
        state.sending_invoice_errors = sending_invoice_errors;
        state.sending_invoice = false;
    },

    updateBillingAccount(state, billingaccount) {
        state.billingaccount = billingaccount;
        applyTemplates(state);
    },

    updateCustomer(state, customer) {
        state.customer = customer;
        state.customer_email_available = !!customer?.billing_address?.emails;
    },

    updateMessage(state, message) {
        state.message = message;
    },

    updateSubject(state, subject) {
        state.subject = subject;
    },

    updateRecipients(state, recipients) {
        state.recipients = recipients;
    },

    updateReceiveCopy(state, receive_copy) {
        state.receive_copy = receive_copy;
    },
    updateSendCopyToBillingEmails(state, send_copy_to_billing_emails) {
        state.send_copy_to_billing_emails = send_copy_to_billing_emails;
    },

    updateReminder(state, reminder) {
        state.reminder = reminder;
        applyTemplates(state);
    },

    updateTemplateSource(state, template_source) {
        state.template_source = template_source;
        applyTemplates(state);
    },
};

const actions = {
    async sendInvoice({ commit, dispatch, state }) {
        const sendparams = {
            receive_copy: state.receive_copy,
            send_copy_to_billing_emails: state.send_copy_to_billing_emails,
            recipients: state.recipients,
            subject: state.subject,
            message: state.message,
            reminder: state.reminder,
        };
        commit('updateSendingInvoice', true);
        try {
            const response = await axios.post(
                `/api/customerorders/${state.order.id}/sendinvoice/`,
                sendparams
            );
            dispatch('session/fetchStats', null, { root: true });
            dispatch('billingstats/fetchAllStats', null, { root: true });
            return response.data;
        } catch (xhr_error) {
            const error = storeUtils.handleError(xhr_error);
            commit('updateSendingInvoiceErrors', error.details);
            throw error;
        }
    },

    async fetchBillingAccount({ commit, state }) {
        try {
            const response = await axios.get(
                `/api/customerorders/${state.order.id}/billingaccount/`
            );
            commit('updateBillingAccount', response.data);
            return response.data;
        } catch (xhr_error) {
            throw storeUtils.handleError(xhr_error);
        }
    },

    async fetchInvoicesToSend({ commit, state }) {
        if (!state.order?.id) {
            commit('updateInvoices', []);
            return [];
        }
        commit('updateInvoicesLoading', true);
        try {
            // Same scope as API order_validated_invoices_queryset: validated
            // invoices on the order or its sub-orders.
            const response = await axios.get(
                `/api/customerorders/${state.order.id}/invoices/`,
                { params: { limit: 200, status: 'validated' } }
            );
            const results = response.data.results || response.data || [];
            // Mirror API order_validated_invoices_queryset:
            // validated + (on suborder OR on master when no suborders needed).
            const masterId = state.order.id;
            const subordersNeeded = !!state.order.suborders_needed;
            const validated = results.filter((invoice) => {
                if (invoice.status !== 'validated') {
                    return false;
                }
                const invoiceOrderId =
                    invoice.order_data?.id ?? invoice.order?.id ?? invoice.order;
                const parentId =
                    invoice.order_data?.parent ?? invoice.order?.parent ?? null;
                if (subordersNeeded) {
                    return String(parentId) === String(masterId);
                }
                return String(invoiceOrderId) === String(masterId);
            });
            commit('updateInvoices', validated);
            return validated;
        } catch (xhr_error) {
            commit('updateInvoices', []);
            throw storeUtils.handleError(xhr_error);
        } finally {
            commit('updateInvoicesLoading', false);
        }
    },

    init({ commit, dispatch }, params) {
        commit('updateReminder', params.reminder || false);
        commit('updateOrder', params?.order);
        commit('updateCustomer', params?.order?.buyer_data);

        const emails = params.order?.buyer_data?.billing_address?.emails;

        commit('updateRecipients', emails || []);
        commit('updateReceiveCopy', false);
        commit('updateSendCopyToBillingEmails', false);
        commit('updateSendingInvoiceErrors', {});
        commit('updateSendingInvoice', false);
        if (params?.order) {
            dispatch('fetchBillingAccount');
            dispatch('fetchInvoicesToSend');
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
